import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaFileInvoice, FaEye, FaUpload, FaTruck,
  FaCalendarAlt, FaWeightHanging, FaDollarSign,
  FaClock, FaCheckCircle, FaTimes, FaSpinner,
  FaBuilding, FaUserTie, FaMapMarkerAlt, FaFileAlt,
  FaDownload, FaPrint, FaSearch, FaFilter,
  FaMoneyBillWave, FaHourglassHalf, FaSync,
  FaPlus, FaTrash, FaInfoCircle, FaCheckDouble,
  FaLink, FaExternalLinkAlt
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import InvoicePDFButton from '../components/InvoicePDFButton';

function AdminPayments({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [podSubmissions, setPodSubmissions] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [user, setUser] = useState({});
  const [subbies, setSubbies] = useState([]);
  const [filters, setFilters] = useState({
    subbieId: '',
    status: '',
    dateRange: 'all',
    startDate: '',
    endDate: ''
  });
  
  // Modal states
  const [showPayModal, setShowPayModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPODModal, setShowPODModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [payLedger, setPayLedger] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Consolidation (merge line payables into one parent → one payment, one QBO bill)
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [merging, setMerging] = useState(false);
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: '',
    paymentMethod: 'bank_transfer',
    amount: '',
    popFile: null,
    popPreview: null,
    notes: ''
  });

  // POD form
  const [podForm, setPodForm] = useState({
    receivedBy: '',
    notes: ''
  });

  // 📎 Attach-POD-to-payable modal — the office uploads the signed POD after the work
  // is done. The document is stored on the load, so it flows straight to the client.
  const [showAttachPodModal, setShowAttachPodModal] = useState(false);
  const [attachingPod, setAttachingPod] = useState(false);
  const [attachPodForm, setAttachPodForm] = useState({
    file: null, url: '', podNumber: '', receiverName: '', podWeight: '', notes: ''
  });

  // ✖️ Cancel payable — custom in-app modal (never a native prompt)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('Cancelled in error');

  // 💸 Record money back — custom in-app modal with proof-of-payment upload
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({
    amount: '',
    recoveryDate: '',
    method: 'bank_transfer',
    reference: '',
    notes: '',
    popFile: null,
    popPreview: null
  });

  // 🖱️ Drag-and-drop highlight state for the two POP upload zones
  const [isPopDragging, setIsPopDragging] = useState(false);
  const [isRecoveryDragging, setIsRecoveryDragging] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchData();
  }, []);

  const userPermissions = user.permissions || [];
  const canApprovePayments = user.role === 'super_admin' || userPermissions.includes('approve_payments');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all subbies
      const subbieRes = await fetch(`${API_URL}/subcontractors`);
      const subbieData = await subbieRes.json();
      if (subbieData.success) {
        setSubbies(subbieData.subcontractors || []);
      }

      // Fetch all subbie invoices for admin
      const invRes = await fetch(`${API_URL}/subbie-invoices/admin`);
      const invData = await invRes.json();
      if (invData.success) {
        setInvoices(invData.invoices || []);
        setFilteredInvoices(invData.invoices || []);
      } else {
        setError(invData.error || 'Failed to fetch invoices');
      }

      // Fetch POD submissions for admin
      const podRes = await fetch(`${API_URL}/subbie-invoices/admin-pod-submissions`);
      const podData = await podRes.json();
      if (podData.success) {
        setPodSubmissions(podData.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending_payment': 'bg-yellow-100 text-yellow-700',
      'partially_paid': 'bg-orange-100 text-orange-700',
      'paid': 'bg-green-100 text-green-700',
      'cancelled': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending_payment': '⏳ Pending Payment',
      'partially_paid': '🟠 Partially Paid',
      'paid': '✅ Paid',
      'cancelled': '✖️ Cancelled'
    };
    return labels[status] || status;
  };

  const getPodStatusBadge = (status) => {
    const styles = {
      'pending_receipt': 'bg-purple-100 text-purple-700',
      'submitted': 'bg-purple-100 text-purple-700',
      'received': 'bg-green-100 text-green-700',
      'not_required': 'bg-gray-200 text-gray-600'
    };
    return `px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const getPodStatusLabel = (status) => {
    const labels = {
      'pending_receipt': '⏳ Pending Receipt',
      'submitted': '📦 POD Submitted',
      'received': '✅ POD Received',
      'not_required': '🚫 POD Not Required'
    };
    return labels[status] || status;
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (currentFilters = filters) => {
    let filtered = [...invoices];

    if (currentFilters.subbieId) {
      filtered = filtered.filter(i => i.subcontractor_id === currentFilters.subbieId);
    }

    if (currentFilters.status) {
      if (currentFilters.status === 'recovery_outstanding') {
        // Cancelled payables we paid but haven't got the money back on yet.
        filtered = filtered.filter(i => i.status === 'cancelled' &&
          ((parseFloat(i.paid_amount) || 0) > (parseFloat(i.recovered_amount) || 0)));
      } else {
        filtered = filtered.filter(i => i.status === currentFilters.status);
      }
    }

    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];

    if (currentFilters.dateRange === 'today') {
      filtered = filtered.filter(i => i.created_at?.startsWith(todayStr));
    } else if (currentFilters.dateRange === 'last7') {
      filtered = filtered.filter(i => {
        const date = i.created_at?.split('T')[0];
        return date >= sevenDaysAgoStr && date <= todayStr;
      });
    } else if (currentFilters.dateRange === 'custom') {
      if (currentFilters.startDate) {
        filtered = filtered.filter(i => i.created_at?.split('T')[0] >= currentFilters.startDate);
      }
      if (currentFilters.endDate) {
        filtered = filtered.filter(i => i.created_at?.split('T')[0] <= currentFilters.endDate);
      }
    }

    setFilteredInvoices(filtered);
  };

  const clearFilters = () => {
    setFilters({
      subbieId: '',
      status: '',
      dateRange: 'all',
      startDate: '',
      endDate: ''
    });
    setFilteredInvoices(invoices);
  };

  const handlePODReceived = async () => {
    if (!selectedItem) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/receive-pod`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          received_by: podForm.receivedBy || user.name || 'Admin',
          notes: podForm.notes || null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ POD received confirmed!');
        setShowPODModal(false);
        setSelectedItem(null);
        setPodForm({ receivedBy: '', notes: '' });
        fetchData();
      } else {
        toast.error(data.error || 'Failed to confirm POD receipt');
      }
    } catch (err) {
      console.error('Error confirming POD:', err);
      toast.error('Failed to confirm POD receipt');
    } finally {
      setProcessing(false);
    }
  };

  // 📎 Attach a POD to a payable (office side). The file goes up through the same
  // /api/uploads used everywhere, then the payable endpoint writes it onto the load
  // (so the client portal shows it) and releases the payment gate.
  const handleAttachPod = async () => {
    if (!selectedItem) return;
    const f = attachPodForm;
    if (!f.file && !f.url && !f.podNumber && !f.receiverName && !f.podWeight) {
      toast.error('Attach the POD document (or give a POD number) first.');
      return;
    }
    setAttachingPod(true);
    try {
      let imageUrl = f.url || '';
      if (f.file) {
        const fd = new FormData();
        fd.append('image', f.file);
        const up = await fetch(`${API_URL}/uploads`, { method: 'POST', body: fd });
        const upData = await up.json();
        if (!upData.success || !upData.url) throw new Error(upData.error || 'Upload failed');
        imageUrl = upData.url;
      }
      const res = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/pod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pod_image_url: imageUrl || null,
          pod_number: f.podNumber || null,
          pod_receiver_name: f.receiverName || null,
          pod_weight_mt: f.podWeight || null,
          notes: f.notes || null,
          admin_id: user.id || 'admin',
          admin_email: user.email || 'admin@system.com'
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'POD attached');
        (data.payload_warnings || []).forEach((w) => toast.info(w));
        setShowAttachPodModal(false);
        setSelectedItem(null);
        setAttachPodForm({ file: null, url: '', podNumber: '', receiverName: '', podWeight: '', notes: '' });
        fetchData();
      } else {
        toast.error(data.error || 'Failed to attach POD');
      }
    } catch (err) {
      console.error('Error attaching POD:', err);
      toast.error(err.message || 'Failed to attach POD');
    } finally {
      setAttachingPod(false);
    }
  };

  const handlePayInvoice = async () => {
    if (!paymentForm.paymentDate) {
      toast.error('Please select payment date');
      return;
    }

    if (!paymentForm.popFile) {
      toast.error('Please upload POP');
      return;
    }

    const balance = Math.max((parseFloat(selectedItem.total_amount) || 0) - (parseFloat(selectedItem.paid_amount) || 0), 0);
    const amount = parseFloat(paymentForm.amount);
    if (!amount || isNaN(amount) || amount <= 0) {
      toast.error('Please enter the amount you are paying');
      return;
    }
    if (amount > balance + 0.001) {
      toast.error(`Amount exceeds the outstanding balance of ${formatCurrency(balance)}`);
      return;
    }

    setProcessing(true);
    try {
      // First upload POP
      const formData = new FormData();
      formData.append('image', paymentForm.popFile);

      const uploadRes = await fetch(`${API_URL}/uploads`, {
        method: 'POST',
        body: formData
      });
      const uploadData = await uploadRes.json();
      
      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to upload POP');
      }

      // Then process payment
      const response = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/pay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_date: paymentForm.paymentDate,
          payment_method: paymentForm.paymentMethod,
          amount,
          pop_url: uploadData.url,
          notes: paymentForm.notes || null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message || '✅ Payment processed successfully!');
        setShowPayModal(false);
        setSelectedItem(null);
        setPaymentForm({ paymentDate: '', paymentMethod: 'bank_transfer', amount: '', popFile: null, popPreview: null, notes: '' });
        fetchData();
      } else {
        toast.error(data.error || 'Failed to process payment');
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      toast.error(err.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  // Validate a POP file and set it on the given form. Shared by the click-to-upload
  // input and the drag-and-drop zone so both enforce exactly the same rules.
  const acceptPopFile = (file, setter) => {
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('application/pdf')) {
      toast.error('Please upload an image or PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    setter(prev => ({ ...prev, popFile: file, popPreview: URL.createObjectURL(file) }));
  };

  const handleFileSelect = (e) => {
    acceptPopFile(e.target.files[0], setPaymentForm);
  };

  const loadLedger = async (invoiceId) => {
    setLoadingLedger(true);
    try {
      const res = await fetch(`${API_URL}/subbie-invoices/${invoiceId}/payments`);
      const data = await res.json();
      if (data.success) setPayLedger(data.payments || []);
    } catch (err) {
      console.error('Error fetching payment ledger:', err);
    } finally {
      setLoadingLedger(false);
    }
  };

  const openInvoiceDetail = async (invoice) => {
    setSelectedItem(invoice);
    setShowDetailModal(true);
    setPayLedger([]);
    loadLedger(invoice.id);
  };

  // How much the subbie still owes back (only meaningful on a cancelled payable)
  // Edge-case VAT override: some subbies quote ex-VAT and do not charge VAT (Virjuan). The VAT
  // checkbox on the payable flips vat_applicable; the backend recalculates vat_amount/total from
  // subtotal. Locked once money has moved - you cannot rewrite VAT on a paid/part-paid/cancelled payable.
  const isVatLocked = (item) => !item
    || (parseFloat(item.paid_amount) || 0) > 0
    || ['paid', 'partially_paid', 'cancelled', 'void'].includes(item.status);

  const toggleVat = async (vatApplicable) => {
    if (!selectedItem) return;
    const prev = selectedItem;
    setSelectedItem({ ...selectedItem, vat_applicable: vatApplicable });
    try {
      const res = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/vat`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vat_applicable: vatApplicable })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not update VAT');
      setSelectedItem(data.invoice);
      toast.success(data.message);
    } catch (err) {
      setSelectedItem(prev);
      toast.error(err.message || 'Could not update VAT');
    }
  };


  const getRecoveryOutstanding = (invoice) => invoice
    ? Math.max((parseFloat(invoice.paid_amount) || 0) - (parseFloat(invoice.recovered_amount) || 0), 0)
    : 0;

  // ✖️ Cancel payable — opens the custom in-app modal
  const openCancelModal = () => {
    setCancelReason('Cancelled in error');
    setShowCancelModal(true);
  };

  const confirmCancelPayable = async () => {
    if (!selectedItem) return;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error('Please give a reason for cancelling');
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(
          data.recovery_outstanding > 0
            ? `Payable cancelled — ${formatCurrency(data.recovery_outstanding)} now owed back from the subbie`
            : 'Payable cancelled'
        );
        setSelectedItem(prev => prev ? { ...prev, status: 'cancelled', cancel_reason: reason } : prev);
        setShowCancelModal(false);
        await loadLedger(selectedItem.id);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to cancel payable');
      }
    } catch (err) {
      console.error('Error cancelling payable:', err);
      toast.error(err.message || 'Failed to cancel payable');
    } finally {
      setProcessing(false);
    }
  };

  // 💸 Record money back — opens the custom in-app modal (with POP upload)
  const openRecoveryModal = () => {
    if (!selectedItem) return;
    const outstanding = getRecoveryOutstanding(selectedItem);
    setRecoveryForm({
      amount: outstanding > 0 ? outstanding.toFixed(2) : '',
      recoveryDate: new Date().toISOString().split('T')[0],
      method: 'bank_transfer',
      reference: '',
      notes: '',
      popFile: null,
      popPreview: null
    });
    setShowRecoveryModal(true);
  };

  const handleRecoveryFileSelect = (e) => {
    acceptPopFile(e.target.files[0], setRecoveryForm);
  };

  const confirmRecovery = async () => {
    if (!selectedItem) return;
    const outstanding = getRecoveryOutstanding(selectedItem);
    const amt = parseFloat(recoveryForm.amount);
    if (!amt || isNaN(amt) || amt <= 0) {
      toast.error('Please enter the amount received back');
      return;
    }
    if (amt > outstanding + 0.001) {
      toast.error(`Amount cannot exceed what is still owed back (${formatCurrency(outstanding)})`);
      return;
    }

    setProcessing(true);
    try {
      // Optional proof of payment for the money coming back in
      let popUrl = null;
      if (recoveryForm.popFile) {
        const formData = new FormData();
        formData.append('image', recoveryForm.popFile);
        const uploadRes = await fetch(`${API_URL}/uploads`, { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Failed to upload proof of payment');
        }
        popUrl = uploadData.url;
      }

      const res = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/recovery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amt,
          recovered_at: recoveryForm.recoveryDate || null,
          method: recoveryForm.method,
          reference: recoveryForm.reference || null,
          notes: recoveryForm.notes || null,
          pop_url: popUrl
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(
          data.recovery_outstanding > 0
            ? `Recovery recorded — ${formatCurrency(data.recovery_outstanding)} still owed back`
            : 'Recovery recorded — all money recovered'
        );
        setSelectedItem(prev => prev ? { ...prev, recovered_amount: data.invoice?.recovered_amount } : prev);
        setShowRecoveryModal(false);
        await loadLedger(selectedItem.id);
        fetchData();
      } else {
        toast.error(data.error || 'Failed to record recovery');
      }
    } catch (err) {
      console.error('Error recording recovery:', err);
      toast.error(err.message || 'Failed to record recovery');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  // Who can join a consolidation?
  //  · an untouched payable (nothing paid, not already a child) → yes; or
  //  · the CONSOLIDATION itself → yes, even if it's already been part-paid.
  //    That's how a late supplementary invoice gets folded in (OPL-0017's R600
  //    joining the R164,000 parent OPL-0009, which had R82,000 against it).
  const isOpenParent = (inv) => Boolean(
    inv
    && inv.is_group_parent
    && !inv.merged_into
    && !['cancelled', 'void', 'paid'].includes(inv.status)
    && (parseFloat(inv.paid_amount) || 0) < (parseFloat(inv.total_amount) || 0)
  );

  const mergeable = (inv) => Boolean(inv) && (
    isOpenParent(inv)
    || (
      !['cancelled', 'void', 'paid'].includes(inv.status)
      && !inv.merged_into
      && !inv.is_group_parent
      && (parseFloat(inv.paid_amount) || 0) === 0
    )
  );

  // Is one of the ticked payables a consolidation we're adding to?
  const mergeTarget = (invoices || []).find(
    (i) => selectedForMerge.includes(i.id) && i.is_group_parent
  ) || null;

  const toggleMergeSelection = (id) => {
    setSelectedForMerge((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const consolidate = async () => {
    if (selectedForMerge.length < 2) return;
    setMerging(true);
    try {
      const res = await fetch(`${API_URL}/subbie-invoices/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payable_ids: selectedForMerge })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Payables consolidated');
        setSelectedForMerge([]);
        fetchData();
      } else {
        toast.error(data.error || 'Consolidation failed');
      }
    } catch (err) {
      console.error('Error consolidating payables:', err);
      toast.error('Consolidation failed');
    } finally {
      setMerging(false);
    }
  };

  const unmerge = async () => {
    if (!selectedItem) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/subbie-invoices/${selectedItem.id}/unmerge`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Unmerged');
        setShowDetailModal(false);
        setSelectedItem(null);
        fetchData();
      } else {
        toast.error(data.error || 'Unmerge failed');
      }
    } catch (err) {
      console.error('Error unmerging payables:', err);
      toast.error('Unmerge failed');
    } finally {
      setProcessing(false);
    }
  };

  const canPay = (invoice) => {
    // Payable unlocks once POD is received — OR when POD is not required
    // (e.g. costs allocated directly on an invoice, or off-system completions).
    // Partial payments are allowed, so partially-paid payables stay payable.
    return (invoice.status === 'pending_payment' || invoice.status === 'partially_paid') &&
      (invoice.pod_status === 'received' || invoice.pod_status === 'not_required');
  };

  const canReceivePOD = (pod) => {
    return pod.status === 'pending_receipt' || pod.status === 'submitted';
  };

  // Calculate days remaining until payment is due
  const getPaymentDueDays = (invoice) => {
    if (!invoice || invoice.status === 'paid') return null;
    
    const paymentTerms = invoice.payment_terms || '30 Days';
    const daysToAdd = parseInt(paymentTerms) || 30;
    
    const createdDate = new Date(invoice.created_at);
    const dueDate = new Date(createdDate);
    dueDate.setDate(dueDate.getDate() + daysToAdd);
    
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  if (loading) {
    return (
      <AdminLayout activeTab="payments" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading payments...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab="payments" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Payments</h2>
          <p className="text-gray-500">{error}</p>
          <button 
            onClick={() => fetchData()} 
            className="mt-4 px-4 py-2 rounded-lg text-white" 
            style={{ backgroundColor: '#00A86B' }}
          >
            Retry
          </button>
        </div>
      </AdminLayout>
    );
  }

  const pendingPODs = podSubmissions.filter(p => canReceivePOD(p));

  return (
    <AdminLayout activeTab="payments" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">💰 Subbie Payments</h1>
          <p className="text-sm text-gray-500">
            {filteredInvoices.length} invoices • {invoices.filter(i => i.status === 'pending_payment').length} pending • {invoices.filter(i => i.status === 'paid').length} paid
          </p>
        </div>
        <button
          onClick={() => fetchData()}
          className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
        >
          <FaSync size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-50">
              <FaFileAlt className="text-purple-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pending Receipt</p>
              <p className="text-xl font-bold text-navy">{pendingPODs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-yellow-50">
              <FaHourglassHalf className="text-yellow-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Pending Payment</p>
              <p className="text-xl font-bold text-navy">{invoices.filter(i => i.status === 'pending_payment').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-50">
              <FaMoneyBillWave className="text-green-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Paid</p>
              <p className="text-xl font-bold text-navy">{invoices.filter(i => i.status === 'paid').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-50">
              <FaFileInvoice className="text-blue-600" size={18} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Invoices</p>
              <p className="text-xl font-bold text-navy">{invoices.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending POD Receipt Section */}
      {pendingPODs.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-navy mb-3">📦 Pending POD Receipt ({pendingPODs.length})</h2>
          <div className="space-y-3">
            {pendingPODs.map((pod) => (
              <div key={pod.id} className="bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <p className="font-bold text-navy">{pod.allocation?.job?.job_number || 'Unknown Job'}</p>
                      <span className={getPodStatusBadge(pod.status)}>{getPodStatusLabel(pod.status)}</span>
                    </div>
                    <p className="text-sm text-gray-600">
                      <FaUserTie className="inline mr-1" /> {pod.subcontractor?.company_name || 'Unknown Subbie'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {pod.allocation?.truck?.registration_number || 'No Truck'} • {pod.allocation?.weighbridge_weight_mt || 0} MT
                    </p>
                    {pod.submission_type === 'courier' && (
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                        {pod.courier_name && <span>📦 {pod.courier_name}</span>}
                        {pod.waybill_number && <span>📋 Waybill: {pod.waybill_number}</span>}
                        {pod.tracking_url && (
                          <a href={pod.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            <FaExternalLinkAlt size={10} /> Track
                          </a>
                        )}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      Submitted: {new Date(pod.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                  {canApprovePayments && (
                    <button
                      onClick={() => { setSelectedItem(pod); setShowPODModal(true); }}
                      className="px-3 py-1.5 text-white rounded-lg text-xs flex items-center gap-1 shadow-sm"
                      style={{
                        backgroundColor: '#7C3AED',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6D28D9'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7C3AED'}
                    >
                      <FaCheckDouble size={12} /> Confirm POD Received
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>

          <select
            value={filters.subbieId}
            onChange={(e) => handleFilterChange('subbieId', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green min-w-[150px]"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="">All Subbies</option>
            {subbies.map(s => (
              <option key={s.id} value={s.id}>{s.company_name}</option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="">All Statuses</option>
            <option value="pending_payment">Pending Payment</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
            <option value="recovery_outstanding">💸 Money Owed Back (Recoveries)</option>
          </select>

          <select
            value={filters.dateRange}
            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
            style={{ borderColor: '#E8ECEF' }}
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="last7">Last 7 Days</option>
            <option value="custom">Custom Range</option>
          </select>

          {filters.dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </>
          )}

          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Consolidation bar — pick 2+ payables to combine into one */}
      {selectedForMerge.length >= 2 && (
        <div className="mb-4 bg-white rounded-xl border p-3 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: '#E8ECEF' }}>
          <span className="text-sm text-gray-600">
            {mergeTarget ? (
              <>🧩 <strong>{selectedForMerge.length - 1}</strong> payable(s) will be ADDED to the existing consolidation{' '}
                <strong>{mergeTarget.invoice_number}</strong> — it keeps its payments, and the balance becomes the new outstanding.</>
            ) : (
              <>🧩 <strong>{selectedForMerge.length}</strong> payables selected — consolidate into ONE payable (single payment + single QBO bill; the lines stay intact for reporting).</>
            )}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedForMerge([])} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs hover:bg-gray-50">Clear</button>
            <button
              onClick={consolidate}
              disabled={merging}
              className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-navy disabled:opacity-50 flex items-center gap-1"
            >
              {merging ? <FaSpinner className="animate-spin" size={12} /> : <FaLink size={12} />} {mergeTarget ? 'Add to consolidation' : 'Consolidate'}
            </button>
          </div>
        </div>
      )}

      {/* Invoices List */}
      {filteredInvoices.length === 0 && pendingPODs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-6xl mb-4">💰</div>
          <h3 className="text-xl font-semibold text-navy">No Payments Found</h3>
          <p className="text-sm text-gray-500">
            No subbie invoices or POD submissions match your current filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInvoices.map((invoice) => {
            const isPaid = invoice.status === 'paid';
            const isPending = invoice.status === 'pending_payment';
            const canProcessPay = canPay(invoice);
            const podStatus = invoice.pod_status || 'pending';
            const daysLeft = getPaymentDueDays(invoice);
            
            return (
              <div key={invoice.id} className={`bg-white rounded-xl border p-6 shadow-sm hover:shadow-md transition-shadow ${isPaid ? 'opacity-80' : ''} ${selectedForMerge.includes(invoice.id) ? 'ring-2 ring-green' : ''}`} style={{ borderColor: '#E8ECEF' }}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      {canApprovePayments && mergeable(invoice) && (
                        <input
                          type="checkbox"
                          title="Select to consolidate with another payable"
                          checked={selectedForMerge.includes(invoice.id)}
                          onChange={() => toggleMergeSelection(invoice.id)}
                          className="w-4 h-4 accent-green cursor-pointer"
                        />
                      )}
                      <h3 className="text-lg font-bold text-navy">{invoice.invoice_number}</h3>
                      <span className={getStatusBadge(invoice.status)}>{getStatusLabel(invoice.status)}</span>
                      <span className={getPodStatusBadge(podStatus)}>
                        POD: {getPodStatusLabel(podStatus)}
                      </span>
                      {invoice.is_group_parent && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#E7F7F0', color: '#065F46' }}>
                          🧩 Consolidated
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      <FaUserTie className="inline mr-1" /> {invoice.subcontractor?.company_name || 'Unknown Subbie'}
                    </p>
                    <p className="text-sm text-gray-600">
                      <FaBuilding className="inline mr-1" /> {invoice.job?.job_number || 'N/A'}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-2">
                      <span className="flex items-center gap-1">
                        <FaWeightHanging size={14} /> {invoice.weight_mt} MT
                      </span>
                      <span className="flex items-center gap-1 font-medium text-navy">
                        <FaMoneyBillWave size={14} /> {formatCurrency(invoice.total_amount)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt size={14} /> {new Date(invoice.created_at).toLocaleDateString()}
                      </span>
                      {invoice.payment_terms && isPending && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <FaClock size={14} /> Terms: {invoice.payment_terms}
                        </span>
                      )}
                    </div>

                    {/* Payment due countdown timer */}
                    {isPending && daysLeft !== null && (
                      <div className="mt-1 text-sm">
                        {daysLeft > 0 ? (
                          <span className="text-blue-600 flex items-center gap-1">
                            <FaClock size={12} /> Payment due in {daysLeft} days
                          </span>
                        ) : daysLeft === 0 ? (
                          <span className="text-orange-500 font-medium flex items-center gap-1">
                            ⚠️ Payment due today!
                          </span>
                        ) : (
                          <span className="text-red-600 font-medium flex items-center gap-1">
                            ⚠️ Payment overdue by {Math.abs(daysLeft)} days!
                          </span>
                        )}
                      </div>
                    )}

                    {isPaid && invoice.payment_date && (
                      <div className="mt-2 text-sm text-green-600">
                        <FaCheckCircle className="inline mr-1" />
                        Paid on: {new Date(invoice.payment_date).toLocaleDateString()}
                        {invoice.payment_method && ` • ${invoice.payment_method}`}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openInvoiceDetail(invoice)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs transition-colors flex items-center gap-1 shadow-sm"
                    >
                      <FaEye size={12} /> View
                    </button>

                    {/* 📎 Attach the POD — the ONE place to add the signed document. Shown
                        while the payable's POD is not yet received (before the payment
                        gate opens), then it disappears because there is nothing to add. */}
                    {canApprovePayments && !isPaid && podStatus !== 'received' && podStatus !== 'not_required' && (
                      <button
                        onClick={() => {
                          setSelectedItem(invoice);
                          setAttachPodForm({ file: null, url: '', podNumber: '', receiverName: '', podWeight: '', notes: '' });
                          setShowAttachPodModal(true);
                        }}
                        className="px-3 py-1.5 text-white rounded-lg text-xs flex items-center gap-1 shadow-sm"
                        style={{ backgroundColor: '#7C3AED', border: 'none', cursor: 'pointer', fontWeight: '500' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6D28D9'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7C3AED'}
                        title="Attach the signed proof of delivery to this payable"
                      >
                        <FaFileAlt size={12} /> POD
                      </button>
                    )}

                    {/* ✅ DOWNLOAD PDF BUTTON - only when paid */}
                    {isPaid && (
                      <InvoicePDFButton 
                        invoice={invoice}
                        job={invoice.job}
                        subbie={invoice.subcontractor}
                      />
                    )}

                    {/* Pay button - only when pending and POD received */}
                    {canProcessPay && canApprovePayments && (
                      <button
                        onClick={() => {
                          setSelectedItem(invoice);
                          setPaymentForm({
                            paymentDate: new Date().toISOString().split('T')[0],
                            paymentMethod: 'bank_transfer',
                            amount: String(Math.max((parseFloat(invoice.total_amount) || 0) - (parseFloat(invoice.paid_amount) || 0), 0).toFixed(2)),
                            popFile: null,
                            popPreview: null,
                            notes: ''
                          });
                          setShowPayModal(true);
                        }}
                        className="px-3 py-1.5 text-white rounded-lg text-xs flex items-center gap-1 shadow-sm"
                        style={{
                          backgroundColor: '#00A86B',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
                      >
                        <FaMoneyBillWave size={12} /> Pay
                      </button>
                    )}

                    {isPaid && invoice.pop_url && (
                      <a
                        href={invoice.pop_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs hover:bg-gray-300 transition-colors flex items-center gap-1"
                      >
                        <FaDownload size={12} /> POP
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 📎 Attach POD to payable */}
      {showAttachPodModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAttachPodModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">📎 Attach POD to {selectedItem.invoice_number}</h2>
              <button onClick={() => setShowAttachPodModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes />
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              The signed proof of delivery is stored on the load and becomes visible to the client.
              Attaching it also marks this payable's POD as received, which unlocks payment.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-1">POD document (image or PDF)</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setAttachPodForm({ ...attachPodForm, file: e.target.files?.[0] || null })}
                  className="w-full text-sm"
                />
                {attachPodForm.file && (
                  <p className="text-xs text-green-600 mt-1">Selected: {attachPodForm.file.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-1">…or paste a document link</label>
                <input
                  type="text"
                  value={attachPodForm.url}
                  onChange={(e) => setAttachPodForm({ ...attachPodForm, url: e.target.value })}
                  placeholder="https://…"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{ borderColor: '#E8ECEF' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-navy mb-1">POD number</label>
                  <input
                    type="text"
                    value={attachPodForm.podNumber}
                    onChange={(e) => setAttachPodForm({ ...attachPodForm, podNumber: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    style={{ borderColor: '#E8ECEF' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-1">Received by</label>
                  <input
                    type="text"
                    value={attachPodForm.receiverName}
                    onChange={(e) => setAttachPodForm({ ...attachPodForm, receiverName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    style={{ borderColor: '#E8ECEF' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-1">POD weight (MT)</label>
                <input
                  type="number"
                  value={attachPodForm.podWeight}
                  onChange={(e) => setAttachPodForm({ ...attachPodForm, podWeight: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{ borderColor: '#E8ECEF' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy mb-1">Notes</label>
                <textarea
                  value={attachPodForm.notes}
                  onChange={(e) => setAttachPodForm({ ...attachPodForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{ borderColor: '#E8ECEF' }}
                  placeholder="Any notes about this POD…"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAttachPodModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAttachPod}
                disabled={attachingPod}
                className="px-4 py-2 rounded-lg text-white text-sm flex items-center gap-2"
                style={{ backgroundColor: attachingPod ? '#9CA3AF' : '#7C3AED', border: 'none' }}
              >
                {attachingPod ? (<><FaSpinner className="animate-spin" size={12} /> Attaching…</>) : (<><FaCheckCircle size={12} /> Attach & release payment</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm POD Received Modal */}
      {showPODModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPODModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📦 Confirm POD Received</h2>
              <button onClick={() => setShowPODModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Job:</span> {selectedItem.allocation?.job?.job_number || 'N/A'}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Subbie:</span> {selectedItem.subcontractor?.company_name || 'N/A'}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Received By</label>
                <input
                  type="text"
                  value={podForm.receivedBy}
                  onChange={(e) => setPodForm({ ...podForm, receivedBy: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder={user.name || 'Admin'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={podForm.notes}
                  onChange={(e) => setPodForm({ ...podForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  rows="2"
                  placeholder="Any notes about the POD receipt..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowPODModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePODReceived}
                disabled={processing}
                className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 shadow-sm"
                style={{
                  backgroundColor: '#7C3AED',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  if (!processing) e.currentTarget.style.backgroundColor = '#6D28D9';
                }}
                onMouseLeave={(e) => {
                  if (!processing) e.currentTarget.style.backgroundColor = '#7C3AED';
                }}
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaCheckDouble size={14} />}
                {processing ? 'Confirming...' : 'Confirm Received'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {showPayModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPayModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">💰 Process Payment</h2>
              <button onClick={() => setShowPayModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Invoice:</span> {selectedItem.invoice_number}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Subbie:</span> {selectedItem.subcontractor?.company_name || 'N/A'}
              </p>
              <p className="text-sm font-medium text-navy">
                <span className="font-medium">Total:</span> {formatCurrency(selectedItem.total_amount)}
              </p>
              {(parseFloat(selectedItem.paid_amount) || 0) > 0 && (
                <p className="text-sm font-medium text-green-600">
                  <span className="font-medium">Already Paid:</span> {formatCurrency(selectedItem.paid_amount)}
                </p>
              )}
              <p className="text-sm font-semibold text-navy">
                <span className="font-medium">Balance:</span> {formatCurrency(Math.max((parseFloat(selectedItem.total_amount) || 0) - (parseFloat(selectedItem.paid_amount) || 0), 0))}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (R) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                />
                <p className="text-xs text-gray-400 mt-1">Defaults to the full balance. Enter less to make a partial payment.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="eft">EFT</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload POP *</label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${isPopDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-500'}`}
                  onClick={() => document.getElementById('pop-upload')?.click()}
                  onDragEnter={(e) => { e.preventDefault(); setIsPopDragging(true); }}
                  onDragOver={(e) => { e.preventDefault(); setIsPopDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsPopDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsPopDragging(false);
                    acceptPopFile(e.dataTransfer?.files?.[0], setPaymentForm);
                  }}
                >
                  <input
                    id="pop-upload"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {paymentForm.popPreview ? (
                    <div className="space-y-2">
                      {paymentForm.popPreview.startsWith('data:image') ? (
                        <img 
                          src={paymentForm.popPreview} 
                          alt="POP Preview" 
                          className="max-h-32 mx-auto rounded-lg object-contain"
                        />
                      ) : (
                        <p className="text-sm text-green-600">📄 PDF uploaded</p>
                      )}
                      <p className="text-sm text-green-600">✅ POP uploaded</p>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setPaymentForm(prev => ({ ...prev, popFile: null, popPreview: null }));
                          document.getElementById('pop-upload').value = '';
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FaUpload className="text-2xl text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        {isPopDragging ? 'Drop POP to upload' : 'Click or drag & drop to upload POP'}
                      </p>
                      <p className="text-xs text-gray-400">JPEG, PNG, PDF (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  rows="2"
                  placeholder="Any notes about this payment..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowPayModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePayInvoice}
                disabled={processing}
                className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 shadow-sm"
                style={{
                  backgroundColor: '#00A86B',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  if (!processing) e.currentTarget.style.backgroundColor = '#00995E';
                }}
                onMouseLeave={(e) => {
                  if (!processing) e.currentTarget.style.backgroundColor = '#00A86B';
                }}
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle size={14} />}
                {processing ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Detail Modal */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDetailModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">📄 Invoice Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-gray-500">Invoice Number</p>
                <p className="font-medium text-navy">{selectedItem.invoice_number}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span className={getStatusBadge(selectedItem.status)}>{getStatusLabel(selectedItem.status)}</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Subcontractor</p>
                <p className="font-medium text-navy">{selectedItem.subcontractor?.company_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Job</p>
                <p className="font-medium text-navy">{selectedItem.job?.job_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Created</p>
                <p className="font-medium text-navy">{new Date(selectedItem.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">POD Status</p>
                <span className={getPodStatusBadge(selectedItem.pod_status)}>
                  {getPodStatusLabel(selectedItem.pod_status)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500">Weight</p>
                <p className="font-medium text-navy">{selectedItem.weight_mt} MT</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Rate</p>
                <p className="font-medium text-navy">R{selectedItem.rate_per_mt}/MT</p>
              </div>
              {selectedItem.vat_applicable !== undefined && (
                <>
                  <div>
                    <p className="text-xs text-gray-500">VAT Applied</p>
                    <label className={`flex items-center gap-2 text-sm font-medium ${isVatLocked(selectedItem) ? 'text-gray-400 cursor-not-allowed' : 'text-navy cursor-pointer'}`}><input type="checkbox" checked={!!selectedItem.vat_applicable} disabled={isVatLocked(selectedItem)} onChange={(e) => toggleVat(e.target.checked)} className="h-4 w-4 accent-green-600 disabled:opacity-50" /> {selectedItem.vat_applicable ? 'Yes (15%)' : 'No (ex-VAT)'}</label>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">VAT Amount</p>
                    <p className="font-medium text-navy">{formatCurrency(selectedItem.vat_amount || 0)}</p>
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-4" style={{ borderColor: '#E8ECEF' }}>
              <h3 className="font-medium text-navy mb-2">Amount Breakdown</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Amount ({selectedItem.weight_mt} MT × R{selectedItem.rate_per_mt})</span>
                  <span className="font-medium">{formatCurrency(selectedItem.base_amount)}</span>
                </div>
                {selectedItem.extras && selectedItem.extras.map((extra, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-gray-600">{extra.description || `Extra ${idx + 1}`}</span>
                    <span className="font-medium">{formatCurrency(extra.amount)}</span>
                  </div>
                ))}
                {selectedItem.subtotal !== undefined && (
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="text-gray-600 font-medium">Subtotal</span>
                    <span className="font-medium">{formatCurrency(selectedItem.subtotal)}</span>
                  </div>
                )}
                {selectedItem.vat_applicable && selectedItem.vat_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT (15%)</span>
                    <span className="font-medium">{formatCurrency(selectedItem.vat_amount)}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span className="text-navy">Total</span>
                  <span className="text-green-600">{formatCurrency(selectedItem.total_amount)}</span>
                </div>
              </div>
            </div>

            {selectedItem.payment_date && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  <FaCheckCircle className="inline mr-2" />
                  Paid on: {new Date(selectedItem.payment_date).toLocaleDateString()}
                  {selectedItem.payment_method && ` • ${selectedItem.payment_method}`}
                </p>
                {selectedItem.pop_url && (
                  <a
                    href={selectedItem.pop_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                  >
                    View POP
                  </a>
                )}
              </div>
            )}

            {/* Payment / recovery summary */}
            {(selectedItem.status === 'partially_paid' || selectedItem.status === 'cancelled' ||
              (parseFloat(selectedItem.paid_amount) || 0) > 0 || (parseFloat(selectedItem.recovered_amount) || 0) > 0) && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid so far</span>
                  <span className="font-medium text-green-700">{formatCurrency(selectedItem.paid_amount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recovered (money back)</span>
                  <span className="font-medium text-gray-700">{formatCurrency(selectedItem.recovered_amount || 0)}</span>
                </div>
                {selectedItem.status === 'cancelled' ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">💸 Still owed back</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(Math.max((parseFloat(selectedItem.paid_amount) || 0) - (parseFloat(selectedItem.recovered_amount) || 0), 0))}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Balance</span>
                    <span className="font-medium text-navy">
                      {formatCurrency(Math.max((parseFloat(selectedItem.total_amount) || 0) - (parseFloat(selectedItem.paid_amount) || 0), 0))}
                    </span>
                  </div>
                )}
                {selectedItem.cancel_reason && (
                  <div className="pt-1 border-t" style={{ borderColor: '#E8ECEF' }}>
                    <p className="text-xs text-gray-500"><span className="font-medium">Cancel reason:</span> {selectedItem.cancel_reason}</p>
                  </div>
                )}
              </div>
            )}

            {/* Payment history ledger */}
            <div className="mt-4 border-t pt-4" style={{ borderColor: '#E8ECEF' }}>
              <h3 className="font-medium text-navy mb-2 flex items-center justify-between">
                <span>Payment History</span>
                {loadingLedger && <span className="text-xs text-gray-400 font-normal">Loading…</span>}
              </h3>
              {!loadingLedger && payLedger.length === 0 ? (
                <p className="text-sm text-gray-400">No payments recorded yet.</p>
              ) : (
                <div className="space-y-2">
                {payLedger.map((p, idx) => {
                  const isRecovery = p.type === 'recovery';
                  return (
                    <div key={p.id || idx} className={`flex items-start justify-between gap-3 p-2 rounded-lg ${isRecovery ? 'bg-orange-50' : 'bg-green-50'}`}>
                      <div>
                        <p className="text-sm font-medium text-navy flex items-center gap-2">
                          {isRecovery ? '💸 Money back' : '💰 Payment'}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isRecovery ? 'bg-orange-200 text-orange-800' : 'bg-green-200 text-green-800'}`}>
                            {p.type}
                          </span>
                        </p>
                        {p.paid_at && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {new Date(p.paid_at.includes('T') ? p.paid_at : `${p.paid_at}T00:00:00`).toLocaleDateString()}
                            {(p.method || p.payment_method) && ` • ${p.method || p.payment_method}`}
                          </p>
                        )}
                        {(p.notes || p.reference) && (
                          <p className="text-xs text-gray-500 mt-0.5">{[p.notes, p.reference].filter(Boolean).join(' • ')}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`text-sm font-semibold ${isRecovery ? 'text-orange-600' : 'text-green-700'}`}>
                          {isRecovery ? '+' : ''}{formatCurrency(p.amount)}
                        </p>
                        {p.pop_url && (
                          <a href={p.pop_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 underline">
                            View POP
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
            </div>

            {canApprovePayments && (
              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                {selectedItem.is_group_parent && (parseFloat(selectedItem.paid_amount) || 0) === 0 && (
                  <button
                    onClick={unmerge}
                    disabled={processing}
                    className="px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-xs transition-colors disabled:opacity-50"
                  >
                    🧩 Unmerge
                  </button>
                )}
                {(selectedItem.status === 'pending_payment' || selectedItem.status === 'partially_paid') && (
                  <button
                    onClick={openCancelModal}
                    disabled={processing}
                    className="px-3 py-1.5 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-xs transition-colors disabled:opacity-50"
                  >
                    ✖️ Cancel Payable
                  </button>
                )}
                {selectedItem.status === 'cancelled' &&
                  (parseFloat(selectedItem.paid_amount) || 0) > (parseFloat(selectedItem.recovered_amount) || 0) && (
                  <button
                    onClick={openRecoveryModal}
                    disabled={processing}
                    className="px-3 py-1.5 border border-orange-300 text-orange-600 hover:bg-orange-50 rounded-lg text-xs transition-colors disabled:opacity-50"
                  >
                    💸 Record Money Back
                  </button>
                )}
              </div>
            )}
            {/* Payment due countdown in detail modal */}
            {selectedItem.status === 'pending_payment' && (
              <div className="mt-4">
                {(() => {
                  const daysLeft = getPaymentDueDays(selectedItem);
                  if (daysLeft === null) return null;
                  const dueDate = new Date(new Date(selectedItem.created_at).getTime() + (parseInt(selectedItem.payment_terms || '30') * 24 * 60 * 60 * 1000)).toLocaleDateString();
                  if (daysLeft > 0) {
                    return (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <FaClock className="text-blue-600" />
                          Payment due in {daysLeft} days (due {dueDate})
                        </p>
                      </div>
                    );
                  } else if (daysLeft === 0) {
                    return (
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                          ⚠️ Payment due today!
                        </p>
                      </div>
                    );
                  } else {
                    return (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                          ⚠️ Payment overdue by {Math.abs(daysLeft)} days!
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {/* ✅ DOWNLOAD PDF BUTTON IN DETAIL MODAL - only when paid */}
            {selectedItem.status === 'paid' && (
              <div className="mt-4 flex justify-end">
                <InvoicePDFButton 
                  invoice={selectedItem}
                  job={selectedItem.job}
                  subbie={selectedItem.subcontractor}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✖️ Cancel Payable Modal — custom in-app modal (never a native prompt) */}
      {showCancelModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!processing) setShowCancelModal(false); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">✖️ Cancel Payable</h2>
              <button onClick={() => setShowCancelModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-1">
              <p className="text-sm text-gray-600"><span className="font-medium">Invoice:</span> {selectedItem.invoice_number}</p>
              <p className="text-sm text-gray-600"><span className="font-medium">Subbie:</span> {selectedItem.subcontractor?.company_name || 'N/A'}</p>
              <p className="text-sm text-gray-600"><span className="font-medium">Total:</span> {formatCurrency(selectedItem.total_amount)}</p>
              {(parseFloat(selectedItem.paid_amount) || 0) > 0 && (
                <p className="text-sm font-semibold text-orange-600">Already paid: {formatCurrency(selectedItem.paid_amount)}</p>
              )}
            </div>

            {(parseFloat(selectedItem.paid_amount) || 0) > 0 && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-xs text-orange-700">
                  <FaInfoCircle className="inline mr-1" />
                  Money already paid stays on record and becomes <strong>money owed back</strong> from the subbie. Capture it afterwards with “💸 Record Money Back”.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancelling *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                placeholder="e.g. Job cancelled, allocated in error, duplicate allocation..."
              />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Keep Payable
              </button>
              <button
                onClick={confirmCancelPayable}
                disabled={processing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm flex items-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaTrash size={14} />}
                {processing ? 'Cancelling...' : 'Cancel Payable'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 💸 Record Money Back Modal — custom form with proof-of-payment upload */}
      {showRecoveryModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!processing) setShowRecoveryModal(false); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 pb-4 border-b" style={{ borderColor: '#E8ECEF' }}>
              <h2 className="text-xl font-bold text-navy">💸 Record Money Back</h2>
              <button onClick={() => setShowRecoveryModal(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={20} />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-1">
              <p className="text-sm text-gray-600"><span className="font-medium">Invoice:</span> {selectedItem.invoice_number}</p>
              <p className="text-sm text-gray-600"><span className="font-medium">Subbie:</span> {selectedItem.subcontractor?.company_name || 'N/A'}</p>
              <p className="text-sm text-gray-600">Paid to subbie: {formatCurrency(selectedItem.paid_amount || 0)} · Recovered: {formatCurrency(selectedItem.recovered_amount || 0)}</p>
              <p className="text-sm font-semibold text-orange-600">Still owed back: {formatCurrency(getRecoveryOutstanding(selectedItem))}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount received back (R) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={recoveryForm.amount}
                  onChange={(e) => setRecoveryForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                />
                <p className="text-xs text-gray-400 mt-1">Defaults to the full amount owed back. Capture part of it for a partial recovery.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date received *</label>
                <input
                  type="date"
                  value={recoveryForm.recoveryDate}
                  onChange={(e) => setRecoveryForm(prev => ({ ...prev, recoveryDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                <select
                  value={recoveryForm.method}
                  onChange={(e) => setRecoveryForm(prev => ({ ...prev, method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="eft">EFT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference (optional)</label>
                <input
                  type="text"
                  value={recoveryForm.reference}
                  onChange={(e) => setRecoveryForm(prev => ({ ...prev, reference: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="e.g. EFT ref 4471"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proof of payment (optional)</label>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${isRecoveryDragging ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-500'}`}
                  onClick={() => document.getElementById('recovery-pop-upload')?.click()}
                  onDragEnter={(e) => { e.preventDefault(); setIsRecoveryDragging(true); }}
                  onDragOver={(e) => { e.preventDefault(); setIsRecoveryDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsRecoveryDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsRecoveryDragging(false);
                    acceptPopFile(e.dataTransfer?.files?.[0], setRecoveryForm);
                  }}
                >
                  <input
                    id="recovery-pop-upload"
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleRecoveryFileSelect}
                    className="hidden"
                  />
                  {recoveryForm.popFile ? (
                    <div className="space-y-2">
                      {recoveryForm.popFile.type === 'application/pdf' ? (
                        <p className="text-sm text-green-600">📄 {recoveryForm.popFile.name}</p>
                      ) : (
                        <img
                          src={recoveryForm.popPreview}
                          alt="Proof of payment preview"
                          className="max-h-32 mx-auto rounded-lg object-contain"
                        />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecoveryForm(prev => ({ ...prev, popFile: null, popPreview: null }));
                          const el = document.getElementById('recovery-pop-upload');
                          if (el) el.value = '';
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FaUpload className="text-2xl text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">
                        {isRecoveryDragging ? 'Drop proof of payment to upload' : 'Click or drag & drop to upload proof of payment'}
                      </p>
                      <p className="text-xs text-gray-400">JPEG, PNG, PDF (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={recoveryForm.notes}
                  onChange={(e) => setRecoveryForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="Any notes about this money coming back..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button
                onClick={() => setShowRecoveryModal(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRecovery}
                disabled={processing}
                className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50 shadow-sm"
                style={{ backgroundColor: '#EA580C', border: 'none', cursor: 'pointer', fontWeight: '500' }}
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaMoneyBillWave size={14} />}
                {processing ? 'Saving...' : 'Record Money Back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminPayments;
